import { useCallback, useContext, useMemo } from 'react';

import { useLocalStorage } from 'usehooks-ts';

import { AuthContext } from '@arrived/auth';
import {
  FEATURE_FLAGS,
  NOTIFICATION_BANNER_MESSAGE_LOCAL_STORAGE_KEY,
  NotificationBannerMessage,
  NotificationBannerVisibilityStatus,
  useFeature,
} from '@arrived/feature-flags';

export function useNotificationBannerContent() {
  const { isAuthenticated } = useContext(AuthContext);
  const notificationBannerFeatureFlag = useFeature(FEATURE_FLAGS.NOTIFICATION_BANNER_MESSAGE);
  // Stores a map of the dismissed message IDs
  const [dismissed, setDismissed] = useLocalStorage<Record<number, boolean>>(
    NOTIFICATION_BANNER_MESSAGE_LOCAL_STORAGE_KEY,
    {}
  );

  const messages = useMemo(
    () => (notificationBannerFeatureFlag.value as NotificationBannerMessage[]) ?? [],
    [notificationBannerFeatureFlag]
  );

  // Finds the first message that is enabled and is not dismissed
  const shownMessage = useMemo<NotificationBannerMessage | undefined>(
    () =>
      messages.find(
        (message) =>
          message.enabled &&
          !dismissed[message.id] &&
          message.visibility !==
            (isAuthenticated
              ? NotificationBannerVisibilityStatus.LOGGED_OUT_ONLY
              : NotificationBannerVisibilityStatus.LOGGED_IN_ONLY)
      ),
    [isAuthenticated, messages, dismissed]
  );

  const handleOnClose = useCallback(() => {
    setDismissed((currentDismissed) => {
      currentDismissed[shownMessage.id] = true;
      return currentDismissed;
    });
  }, [dismissed, shownMessage]);

  return useMemo(
    () => ({
      shownMessage: shownMessage,
      handleOnClose,
    }),
    [shownMessage, handleOnClose]
  );
}
